import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { configureApi } from '@round/api';
import { installPreloadErrorHandler } from '@round/utils';
import { datadogRum } from '@datadog/browser-rum';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import './index.css';
import App from './App';
import { queryClient } from './queryClient';
import { WaveAuthProvider } from './auth/WaveAuthContext';
import packageJson from '../package.json';

configureApi({ origin: import.meta.env.VITE_API_DOMAIN ?? '' });
installPreloadErrorHandler({ onError: (error) => console.error('[preloadError]', error) });

// PROD-gated on the client token, so it stays inert until the Datadog RUM app
// and its secrets exist (see ROU-889).
const datadogClientToken = import.meta.env.VITE_DD_WAVE_FRONTEND_CLIENT_TOKEN;
if (import.meta.env.PROD && !!datadogClientToken) {
    datadogRum.init({
        applicationId: import.meta.env.VITE_DD_WAVE_FRONTEND_APPLICATION_ID || '',
        clientToken: import.meta.env.VITE_DD_WAVE_FRONTEND_CLIENT_TOKEN || '',
        site: import.meta.env.VITE_DD_WAVE_FRONTEND_SITE || '',
        service: import.meta.env.VITE_DD_WAVE_FRONTEND_SERVICE || '',
        env: import.meta.env.VITE_DD_WAVE_FRONTEND_ENV || '',
        version: packageJson.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        // Drop error events with no frame pointing at our own bundle: browser
        // extensions / cross-origin scripts fire window.onerror events that
        // Datadog attributes to our views, polluting error tracking (ROU-1189).
        beforeSend: (event) => {
            if (event.type !== 'error') {
                return true;
            }

            try {
                const stack = event.error?.stack;
                if (!stack) {
                    return false;
                }

                // Keep only if some frame's URL resolves to our exact origin.
                // Parse each frame rather than substring-matching the origin, so
                // prefix collisions (e.g. app.wave.me.evil.com) don't slip through
                // and extension frames (safari-web-extension://) are excluded.
                const frameUrls = stack.match(/https?:\/\/[^\s'")]+/g) ?? [];
                return frameUrls.some((frameUrl) => {
                    try {
                        return new URL(frameUrl).origin === window.location.origin;
                    } catch {
                        return false;
                    }
                });
            } catch {
                // Never throw inside beforeSend; fail open so we don't lose real errors.
                return true;
            }
        },
    });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <WaveAuthProvider>
                    <App />
                    <ToastContainer />
                </WaveAuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    </React.StrictMode>
);
